import type { Translate } from "../../../shared/i18n/i18n";

// Корзины группировки на стороне интерфейса: границу считает база
// (impacto_group_bucket_value), сюда приходит уже готовое начало корзины —
// остаётся показать его человеку и разложить группы по порядку.

export type GroupBucketUnit = "day" | "month" | "quarter" | "week" | "year";
export type GroupBucket = { kind: "date"; unit: GroupBucketUnit } | { kind: "number"; size: number };

export const groupBucketUnits: GroupBucketUnit[] = ["day", "week", "month", "quarter", "year"];

const isoDate = /^\d{4}-\d{2}-\d{2}$/;

// Какие корзины вообще предлагать. Формула может вернуть что угодно, поэтому
// вид корзины выводится из тех значений, которые уже пришли: диапазон дат
// бессмыслен для текста, а числовой шаг — для дат.
export function groupBucketKindForValues(values: string[]): GroupBucket["kind"] | null {
  const filled = values.filter((value) => value !== "");
  if (filled.length === 0) return null;
  if (filled.every((value) => isoDate.test(value))) return "date";
  if (filled.every((value) => value.trim() !== "" && Number.isFinite(Number(value)))) return "number";
  return null;
}

// Шаги предлагаются от текущего разброса, а не списком «1, 10, 100»: у оценки
// в часах и у процента выполнения полезные шаги разные. Выбранный шаг после
// этого хранится в виде и сам по себе уже не меняется.
export function suggestNumberBucketSizes(values: string[]): number[] {
  const numbers = values.map(Number).filter((value) => Number.isFinite(value));
  if (numbers.length === 0) return [1, 5, 10];
  const span = Math.max(...numbers) - Math.min(...numbers);
  if (span <= 0) return [1, 5, 10];
  const sizes = new Set<number>();
  // Целимся примерно в 5-15 корзин и берём «круглые» шаги 1/2/5 × 10^n.
  for (const target of [5, 8, 12]) {
    sizes.add(niceStep(span / target));
  }
  return [...sizes].sort((first, second) => first - second).slice(0, 4);
}

function niceStep(raw: number): number {
  const magnitude = 10 ** Math.floor(Math.log10(raw));
  const normalized = raw / magnitude;
  const step = normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10;
  return Number((step * magnitude).toPrecision(12));
}

export function formatGroupBucketLabel(bucket: GroupBucket, value: string, locale: string, t: Translate): string {
  if (bucket.kind === "number") {
    const start = Number(value);
    if (!Number.isFinite(start)) return value;
    return t("board.groupBucketRange", {
      end: formatNumber(start + bucket.size, locale),
      start: formatNumber(start, locale),
    });
  }
  if (!isoDate.test(value)) return value;
  const start = new Date(`${value}T00:00:00`);
  if (Number.isNaN(start.getTime())) return value;
  switch (bucket.unit) {
    case "day":
      return start.toLocaleDateString(locale, { day: "numeric", month: "long", year: "numeric" });
    case "week": {
      const end = new Date(start);
      end.setDate(end.getDate() + 6);
      return t("board.groupBucketRange", {
        end: end.toLocaleDateString(locale, { day: "numeric", month: "short", year: "numeric" }),
        start: start.toLocaleDateString(locale, { day: "numeric", month: "short" }),
      });
    }
    case "month":
      return start.toLocaleDateString(locale, { month: "long", year: "numeric" });
    case "quarter":
      return t("board.groupBucketQuarter", { quarter: Math.floor(start.getMonth() / 3) + 1, year: start.getFullYear() });
    case "year":
      return String(start.getFullYear());
  }
}

function formatNumber(value: number, locale: string): string {
  return value.toLocaleString(locale, { maximumFractionDigits: 4 });
}

// Корзины упорядочены по своему началу, а не по подписи: «10 – 20» лексически
// стоит перед «9 – 10», а «Март 2026» — перед «Январь 2027».
export function compareGroupBucketValues(bucket: GroupBucket, first: string, second: string): number {
  if (first === second) return 0;
  // Карточки без значения держим первыми: это не корзина, а её отсутствие.
  if (first === "") return -1;
  if (second === "") return 1;
  if (bucket.kind === "number") {
    return Number(first) - Number(second);
  }
  return first.localeCompare(second);
}
